/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Stethoscope, ChevronRight, Info, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { urgencyMeta, likelihoodMeta, actionMeta } from './meta';
import { RedFlagBanner } from './RedFlagBanner';

export const AssistantCard = ({ assistant }) => {
  if (!assistant) return null;
  const uMeta = urgencyMeta[assistant.urgency_level] || urgencyMeta.medium;
  const aMeta = actionMeta[assistant.recommended_action];
  const ActionIcon = aMeta?.icon || Info;

  return (
    <div className="space-y-3">
      <RedFlagBanner assistant={assistant} />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={cn('gap-1', uMeta.color)} data-testid="symptom-urgency-badge">
              <Activity className="h-3 w-3" /> Urgenza: {uMeta.label}
            </Badge>
            {assistant.specialty_suggestion && (
              <Badge variant="outline" className="gap-1">
                <Stethoscope className="h-3 w-3" /> {assistant.specialty_suggestion}
              </Badge>
            )}
            {aMeta && (
              <Badge variant="outline" className={cn('gap-1', aMeta.cls)}>
                <ActionIcon className="h-3 w-3" /> {aMeta.label}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {assistant.message && (
            <div className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="symptom-assistant-message">{assistant.message}</div>
          )}

          {assistant.possible_conditions?.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Ipotesi possibili</div>
                <ul className="space-y-2">
                  {assistant.possible_conditions.map((c) => (
                    <li key={`${c.name}-${c.likelihood}`} className="flex items-start gap-2 text-sm">
                      <span className={cn('inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase', likelihoodMeta[c.likelihood] || likelihoodMeta.medium)}>{c.likelihood || 'medium'}</span>
                      <div><span className="font-medium">{c.name}</span>{c.rationale && <span className="text-muted-foreground"> — {c.rationale}</span>}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {assistant.follow_up_questions?.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Domande di follow-up</div>
                <ul className="space-y-1 text-sm">
                  {assistant.follow_up_questions.map((q) => (
                    <li key={q} className="flex items-start gap-2"><ChevronRight className="h-3.5 w-3.5 mt-0.5 text-primary" />{q}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {assistant.disclaimer && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">{assistant.disclaimer}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
